/**
 * Canonical platform-placement matrix — THE single source of truth for
 * per-platform-per-placement aspect ratios, accept windows, safe-zone chrome,
 * and crop behavior. Consumed by BOTH runtimes:
 *
 *   • Client (Vite/React/TS) — imports this module directly.
 *   • Edge (Supabase Deno)   — imports it via `supabase/functions/_shared/
 *     placement-matrix.ts`, a one-line re-export (the same pattern
 *     `_shared/model-constants.ts` uses for `model-constants.ts`).
 *
 * HARD CONSTRAINT: this module must stay React-free, DOM-free, Node-free, and
 * `@/`-alias-free so Deno can typecheck it through the re-export stub.
 *
 * Adding a future platform/placement = appending one row here. The invariant
 * tests in `__tests__/` validate every new row against the rules its data
 * implies (recommended ∈ window, options ⊆ window, previewKind ∈ the real
 * union, etc.), so drift is caught the moment a bad row lands.
 *
 * Source spec: docs/superpowers/specs/2026-06-22-composer-accurate-preview-design.md (§2)
 */

/** Canonical aspect-ratio short forms used across picker, preview, validation. */
// NOTE: '3:1' was removed 2026-08-01. It was a declared token with copy in the
// crop picker, offered by NO placement row, and with no compiled Tailwind
// utility behind it — so the day anyone added it to a `ratioOptions` array it
// would have rendered a zero-height preview. Re-add it only together with a
// real rule, and prefer an inline ratio over a class.
export type AspectRatioToken = '1:1' | '4:5' | '1.91:1' | '9:16' | '16:9' | '2:3' | '3:4';

export type Platform =
  'instagram' | 'facebook' | 'tiktok' | 'linkedin' | 'x' | 'youtube' | 'pinterest' | 'threads';

/**
 * Discriminates a surface WITHIN a platform — the axis the old single-window
 * -per-platform model could not express (Reels chrome ≠ feed).
 */
export type Placement =
  'feed' | 'reel' | 'story' | 'short' | 'pin' | 'landscape' | 'document' | 'photo';

/**
 * Bridges a matrix row to the platform-preview replica set. MUST stay a subset
 * of `PlatformPreviewKind` (`src/components/ui/platform-preview/types.ts`);
 * the invariant test pins this.
 */
export type PreviewKind =
  | 'instagram-post'
  | 'instagram-reel'
  | 'instagram-story'
  | 'facebook-post'
  | 'facebook-reel'
  | 'facebook-story'
  | 'tiktok'
  | 'tiktok-photo'
  | 'linkedin-post'
  | 'x-post'
  | 'youtube-short'
  | 'youtube-landscape'
  | 'pinterest-pin'
  | 'threads-post';

export type Confidence = 'verified' | 'documented' | 'estimate';

export interface PlacementRow {
  platform: Platform;
  placement: Placement;
  previewKind: PreviewKind;

  /** Picker pre-selection AND the AI generate-target. Always `ratioOptions[0]`. */
  recommendedRatio: AspectRatioToken;
  /** Discrete chips the picker offers (recommended first). */
  ratioOptions: readonly AspectRatioToken[];

  /** Accept window as numeric W/H. Outside `[acceptMin, acceptMax]` the PLATFORM hard-crops. */
  acceptMin: number;
  acceptMax: number;

  /**
   * Fractions (0–1) of the DISPLAYED frame covered by platform UI chrome.
   * Used only by the safe-zone overlay, never by crop math. Recompute pixels
   * from real asset dimensions at render time — never store pixels.
   */
  safeZones: { top: number; bottom: number; side: number };
  /** Confidence for the safeZones numbers specifically (mostly `estimate` → "approx." in UI). */
  safeZoneConfidence: Confidence;

  /** Platform paints chrome over the media (Reels/Stories/Shorts/TikTok). */
  hasChrome: boolean;
  /** Ratio-locked placement; Mora never crops it (publish passthrough). */
  videoOnly: boolean;

  /** Confidence for the ratio/accept-window data (drives publish behavior). */
  confidence: Confidence;
  /** Vendor/source backing the row. */
  sourceUrl: string;
}

const META_FEED = 'https://developers.facebook.com/docs/instagram-platform/content-publishing';
const META_VERTICAL = 'https://www.facebook.com/business/help/980593475366490';
const LINKEDIN_SPEC = 'https://www.linkedin.com/help/linkedin/answer/a564109';
const X_SPEC = 'https://help.x.com/en/using-x/x-videos';
const YT_SPEC = 'https://support.google.com/youtube/answer/6375112';
const TIKTOK_SPEC = 'https://ads.tiktok.com/help/article/safe-zone-guide';
const TIKTOK_PHOTO_SPEC =
  'https://developers.tiktok.com/doc/content-posting-api-reference-photo-post';
const PIN_SPEC = 'https://help.pinterest.com/en/business/article/pin-and-board-best-practices';
/** Meta's PRIMARY Threads doc — supersedes the third-party blogs the old Threads row came from. */
const THREADS_SPEC = 'https://developers.facebook.com/documentation/threads/posts';

/**
 * THE matrix. One row per (platform × placement). `safeZones` are frame
 * fractions; chrome rows carry non-zero bands, feed/landscape/pin rows are 0.
 */
export const PLACEMENT_MATRIX = [
  {
    platform: 'instagram',
    placement: 'feed',
    previewKind: 'instagram-post',
    recommendedRatio: '4:5',
    ratioOptions: ['4:5', '1:1', '1.91:1'],
    acceptMin: 0.8,
    acceptMax: 1.91,
    safeZones: { top: 0, bottom: 0, side: 0 },
    safeZoneConfidence: 'documented',
    hasChrome: false,
    videoOnly: false,
    confidence: 'verified',
    sourceUrl: META_FEED,
  },
  {
    platform: 'instagram',
    placement: 'reel',
    previewKind: 'instagram-reel',
    recommendedRatio: '9:16',
    ratioOptions: ['9:16'],
    acceptMin: 0.5625,
    acceptMax: 0.5625,
    safeZones: { top: 0.14, bottom: 0.2, side: 0.06 },
    safeZoneConfidence: 'estimate',
    hasChrome: true,
    videoOnly: true,
    confidence: 'verified',
    sourceUrl: META_VERTICAL,
  },
  {
    platform: 'instagram',
    placement: 'story',
    previewKind: 'instagram-story',
    recommendedRatio: '9:16',
    ratioOptions: ['9:16'],
    acceptMin: 0.5625,
    acceptMax: 0.5625,
    safeZones: { top: 0.14, bottom: 0.18, side: 0.06 },
    safeZoneConfidence: 'estimate',
    hasChrome: true,
    videoOnly: true,
    confidence: 'verified',
    sourceUrl: META_VERTICAL,
  },
  {
    platform: 'facebook',
    placement: 'feed',
    previewKind: 'facebook-post',
    recommendedRatio: '1.91:1',
    ratioOptions: ['1.91:1', '1:1', '4:5'],
    acceptMin: 0.5,
    acceptMax: 1.91,
    safeZones: { top: 0, bottom: 0, side: 0 },
    safeZoneConfidence: 'documented',
    hasChrome: false,
    videoOnly: false,
    confidence: 'verified',
    sourceUrl: META_FEED,
  },
  {
    platform: 'facebook',
    placement: 'reel',
    previewKind: 'facebook-reel',
    recommendedRatio: '9:16',
    ratioOptions: ['9:16'],
    acceptMin: 0.5625,
    acceptMax: 0.5625,
    safeZones: { top: 0.14, bottom: 0.35, side: 0.06 },
    safeZoneConfidence: 'estimate',
    hasChrome: true,
    videoOnly: true,
    confidence: 'verified',
    sourceUrl: META_VERTICAL,
  },
  {
    platform: 'facebook',
    placement: 'story',
    previewKind: 'facebook-story',
    recommendedRatio: '9:16',
    ratioOptions: ['9:16'],
    acceptMin: 0.5625,
    acceptMax: 0.5625,
    safeZones: { top: 0.14, bottom: 0.2, side: 0.06 },
    safeZoneConfidence: 'estimate',
    hasChrome: true,
    videoOnly: true,
    confidence: 'verified',
    sourceUrl: META_VERTICAL,
  },
  {
    platform: 'linkedin',
    placement: 'feed',
    previewKind: 'linkedin-post',
    recommendedRatio: '1.91:1',
    ratioOptions: ['1.91:1', '1:1', '4:5'],
    acceptMin: 0.8,
    acceptMax: 3.0,
    safeZones: { top: 0, bottom: 0, side: 0 },
    safeZoneConfidence: 'documented',
    hasChrome: false,
    videoOnly: false,
    confidence: 'verified',
    sourceUrl: LINKEDIN_SPEC,
  },
  {
    platform: 'x',
    placement: 'feed',
    previewKind: 'x-post',
    recommendedRatio: '16:9',
    ratioOptions: ['16:9', '1:1', '1.91:1'],
    acceptMin: 0.5,
    acceptMax: 3.0,
    safeZones: { top: 0, bottom: 0, side: 0 },
    safeZoneConfidence: 'documented',
    hasChrome: false,
    videoOnly: false,
    confidence: 'verified',
    sourceUrl: X_SPEC,
  },
  {
    // Short FIRST so getPlacement('youtube') with no placement defaults to the
    // social-first vertical surface (matches the old picker's youtube→9:16 lock).
    platform: 'youtube',
    placement: 'short',
    previewKind: 'youtube-short',
    recommendedRatio: '9:16',
    ratioOptions: ['9:16'],
    acceptMin: 0.5625,
    acceptMax: 0.5625,
    safeZones: { top: 0.08, bottom: 0.23, side: 0.13 },
    safeZoneConfidence: 'estimate',
    hasChrome: true,
    videoOnly: true,
    confidence: 'verified',
    sourceUrl: YT_SPEC,
  },
  {
    platform: 'youtube',
    placement: 'landscape',
    previewKind: 'youtube-landscape',
    recommendedRatio: '16:9',
    ratioOptions: ['16:9'],
    // ~16:9 (= 1.7778) ± a small tolerance; YouTube pillarboxes off-ratio
    // landscape. A bracket avoids float-equality brittleness vs 16/9.
    acceptMin: 1.74,
    acceptMax: 1.82,
    safeZones: { top: 0, bottom: 0, side: 0 },
    safeZoneConfidence: 'documented',
    hasChrome: false,
    videoOnly: true,
    confidence: 'verified',
    sourceUrl: YT_SPEC,
  },
  // Locked to exactly 9:16, not the full documented 1:1/9:16/16:9 video
  // window (see the photo row below) — every Mora-generated video is
  // 9:16-only today, so this is a deliberate product guard, not an
  // unverified vendor limit. Revisit if a non-vertical video path ships.
  {
    platform: 'tiktok',
    placement: 'feed',
    previewKind: 'tiktok',
    recommendedRatio: '9:16',
    ratioOptions: ['9:16'],
    acceptMin: 0.5625,
    acceptMax: 0.5625,
    safeZones: { top: 0.07, bottom: 0.25, side: 0.13 },
    safeZoneConfidence: 'verified',
    hasChrome: true,
    videoOnly: true,
    confidence: 'verified',
    sourceUrl: TIKTOK_SPEC,
  },
  {
    platform: 'tiktok',
    placement: 'photo',
    // Own kind, NOT 'tiktok' — the drift guard (`drift.test.ts`, "every
    // previewKind is unique") enforces one previewKind per row because
    // `SafeZoneOverlay`/`PlatformPreview` key off previewKind alone, and this
    // row's hasChrome (false) genuinely differs from the video 'feed' row's
    // (true). Reusing 'tiktok' would silently paint reel-style chrome over a
    // photo carousel preview.
    previewKind: 'tiktok-photo',
    recommendedRatio: '9:16',
    ratioOptions: ['9:16', '1:1', '16:9'],
    acceptMin: 0.5625,
    acceptMax: 1.7778,
    safeZones: { top: 0, bottom: 0, side: 0 },
    safeZoneConfidence: 'documented',
    hasChrome: false,
    videoOnly: false,
    confidence: 'documented',
    sourceUrl: TIKTOK_PHOTO_SPEC,
  },
  {
    platform: 'pinterest',
    placement: 'pin',
    previewKind: 'pinterest-pin',
    recommendedRatio: '2:3',
    ratioOptions: ['2:3', '1:1', '9:16'],
    acceptMin: 0.5,
    acceptMax: 1.5,
    safeZones: { top: 0, bottom: 0, side: 0 },
    safeZoneConfidence: 'documented',
    hasChrome: false,
    videoOnly: false,
    confidence: 'verified',
    sourceUrl: PIN_SPEC,
  },
  {
    // Corrected 2026-07-25 against Meta's PRIMARY Threads doc. This row
    // previously read acceptMin 0.8 / acceptMax 1.91 with ratioOptions
    // ['4:5','1:1','1.91:1'] and confidence 'estimate' — copied from the same
    // assumption as the Instagram feed row. That was wrong twice over: Threads
    // documents an accept window of 0.01:1 to 10:1 (i.e. it accepts nearly
    // anything and only RECOMMENDS 9:16 "to avoid cropping or blank space"),
    // and 9:16 — Meta's own recommendation — was not even offered by the ratio
    // switcher.
    //
    // CAVEAT on acceptMin/acceptMax, which this type documents as "outside this
    // the PLATFORM hard-crops": Meta publishes the API *accept* window, not the
    // in-feed display/crop window, and does not document the latter separately.
    // These values therefore model the documented accept window — so the crop
    // signal is deliberately permissive for Threads rather than inventing a
    // tighter number Meta never stated.
    platform: 'threads',
    placement: 'feed',
    previewKind: 'threads-post',
    recommendedRatio: '4:5',
    ratioOptions: ['4:5', '1:1', '9:16', '1.91:1'],
    acceptMin: 0.01,
    acceptMax: 10,
    safeZones: { top: 0, bottom: 0, side: 0 },
    safeZoneConfidence: 'documented',
    hasChrome: false,
    videoOnly: false,
    confidence: 'documented',
    sourceUrl: THREADS_SPEC,
  },
] as const satisfies readonly PlacementRow[];

/** Parse an aspect token to its numeric W/H value (e.g. '9:16' → 0.5625). */
export function ratioToNumber(token: AspectRatioToken): number {
  const [w, h] = token.split(':').map(Number);
  return w / h;
}

/** All rows for a platform. */
export function getPlacementsForPlatform(platform: Platform): readonly PlacementRow[] {
  return PLACEMENT_MATRIX.filter((r) => r.platform === platform);
}

/**
 * The row for a platform's placement. With no `placement`, returns the
 * platform's primary surface (the first matrix row for that platform).
 */
export function getPlacement(platform: Platform, placement?: Placement): PlacementRow | undefined {
  const rows = getPlacementsForPlatform(platform);
  if (placement) return rows.find((r) => r.placement === placement);
  // Primary surface = first row (matrix is ordered primary-first per platform,
  // e.g. youtube short before landscape, instagram feed before reel/story).
  return rows[0];
}

/** The row backing a preview replica kind. */
export function getPlacementByPreviewKind(kind: PreviewKind): PlacementRow | undefined {
  return PLACEMENT_MATRIX.find((r) => r.previewKind === kind);
}

const PLATFORM_ALIASES: Record<string, Platform> = {
  twitter: 'x',
  ig: 'instagram',
  fb: 'facebook',
  li: 'linkedin',
  yt: 'youtube',
  tt: 'tiktok',
};

/**
 * Normalize a free-form platform string (connection rows, picker props) to a
 * canonical matrix `Platform`, or `undefined` if unknown. Handles the
 * `twitter`→`x` alias so the edge lookup never misses legacy-keyed rows.
 */
export function toMatrixPlatform(value: string | null | undefined): Platform | undefined {
  if (!value) return undefined;
  const k = value.toLowerCase().trim();
  if (PLATFORM_ALIASES[k]) return PLATFORM_ALIASES[k];
  return PLACEMENT_MATRIX.some((r) => r.platform === k) ? (k as Platform) : undefined;
}

/**
 * The broadest accept window for a platform — the union across all its
 * placements (a ratio valid for ANY placement is displayable by the platform).
 * This is what publish-side `validateRatioForPlatform` checks so it never
 * false-rejects a ratio that some placement accepts (e.g. IG 9:16 = a Reel).
 * Returns `null` for unknown platforms (caller should not block).
 */
export function getPlatformAcceptWindow(
  platform: string | null | undefined,
): { min: number; max: number } | null {
  const p = toMatrixPlatform(platform);
  if (!p) return null;
  const rows = getPlacementsForPlatform(p);
  if (rows.length === 0) return null;
  return {
    min: Math.min(...rows.map((r) => r.acceptMin)),
    max: Math.max(...rows.map((r) => r.acceptMax)),
  };
}
